import { z } from "zod";
import { FileType as FileTypeSchema } from "../../lib/fileTypes";
import {
  AttributeTestColumnSchema,
  AttributeTestColumnResultSchema,
} from "./attributeTestColumn";
import { AnalyticsColumnSchema } from "./analyticsColumn";
import { AnalyticsColumnResultSchema } from "./analyticsColumnResult";
import { ChildTypeEnum } from "./sessionSupplementaryDocument";
import { lifecycleStateSchema, testsStateSchema } from "./lifecycleState";

const Coordinate = z.tuple([z.number(), z.number()]);
// Base validators
const JSONPrimitiveSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.null(),
]);

export const timestampFields = {
  createdAt: z.string(),
  updatedAt: z.string(),
};

// Document related schemas
export const requiredDocumentSchema = z.object({
  id: z.string(),
  shortName: z.string(),
  description: z.string().nullable(),
  isRequired: z.boolean().optional().nullable(),
  ...timestampFields,
});

// Re-export FileType from centralized configuration
export const FileType = FileTypeSchema;
export type FileType = z.infer<typeof FileType>;

export const sourceDocumentSchema = z.object({
  id: z.string(),
  fileName: z.string(),
  fileType: FileType,
  fileUrl: z.string(),
  fileContentHash: z.string(),
  parentDocumentId: z.string().nullable().optional(),
  childType: ChildTypeEnum.nullable().optional(),
  state: z.string(),
  isParsed: z.boolean().default(false).optional(),
});

// Audit check related schemas
export const auditCheckSchema = z.object({
  id: z.string(),
  name: z.string(),
  agent: z.string(),
  instructions: z.string(),
  tools: z.array(z.string()),
  documents: z.array(requiredDocumentSchema),
  ...timestampFields,
});

// Column related schemas
export const supportColumnSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  dataType: z.string(),
  procedureRequiredDocuments: z
    .array(requiredDocumentSchema)
    .nullable()
    .default([]),
  ...timestampFields,
});

export const evaluatedColumnSchema = z.object({
  id: z.string(),
  name: z.string(),
  instructions: z.string(),
  dataType: z.string().default("string"),
  dependencies: z.array(supportColumnSchema).default([]),
  ...timestampFields,
});

export const procedureColumnSchema = z.object({
  id: z.string(),
  columnType: z.string(),
  columnOrder: z.number(),
  columnVersion: z.number().default(0),
  supportColumn: supportColumnSchema.nullable().optional(),
  evaluatedColumn: evaluatedColumnSchema.nullable().optional(),
  attributeTestColumn: AttributeTestColumnSchema.nullable().optional(),
  analyticsColumn: AnalyticsColumnSchema.nullable().optional(),
  attributeId: z.string().uuid().nullable().optional(),
  // Generated work-step procedure columns carry this instead of attributeId; without it a client column round-trip would null the link.
  workStepAttributeId: z.string().nullable().optional(),
  ...timestampFields,
});

// Procedure related schemas
const procedureAttributeSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.string(),
  description: z.string().nullable(),
  matchStrategy: z.string().nullable(),
  fieldType: z.string().nullable(),
  weight: z.number().nullable(),
  sourceRowDisplay: z.boolean(),
  attributeOrder: z.number().nullable().optional(),
  ...timestampFields,
});

export const dryProcedureSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable().default(""),
  procedureType: z.enum(["control", "substantive"]),
  procedureBasis: z.enum(["sample", "direct"]),
  procedureFlavor: z.string().default("transaction"),
  procedureOrder: z.number().default(1000),
  midshipVersion: z.number(),
  isTemplate: z.boolean(),
  workspaceId: z.string(),
  createdBy: z.string().optional().nullable(),
  controlId: z.string().uuid().nullable().optional(),
  ipeProcedureId: z.string().nullable().optional(),
  complexity: z.enum(["low", "medium", "high"]).nullable().optional(),
  ...timestampFields,
});

export const dryProcedureWithUserSchema = dryProcedureSchema.extend({
  user: z.object({
    id: z.string(),
    email: z.string(),
    createdAt: z.string(),
  }),
});

export const drySessionSchema = z.object({
  id: z.string(),
  procedureId: z.string(),
  name: z.string().optional(),
  controlSessionId: z.string().optional().nullable(),
  state: lifecycleStateSchema,
  documentsState: lifecycleStateSchema,
  testsState: testsStateSchema,
  sourceRowsState: lifecycleStateSchema,
  workPaperState: lifecycleStateSchema,
  ...timestampFields,
});

export const drySessionWithUserSchema = drySessionSchema.extend({
  user: z
    .object({
      id: z.string(),
      email: z.string(),
      createdAt: z.string(),
    })
    .nullable()
    .optional(),
});

// intermediate type to avoid creating recursive type def
export const procedureSchemaWithChildSessions = dryProcedureSchema.extend({
  childSessions: z.array(drySessionWithUserSchema).nullable().optional(),
});

export const procedureSchema = dryProcedureSchema.extend({
  parent: procedureSchemaWithChildSessions.nullable().optional(),
  children: z.array(procedureSchemaWithChildSessions).nullable().optional(),
  childSessions: z.array(drySessionWithUserSchema).nullable().optional(),
  attributes: z.array(procedureAttributeSchema),
  attributeLookup: z.record(procedureAttributeSchema),
  requiredDocuments: z.array(requiredDocumentSchema),
  isEnabled: z.boolean().default(true),
  checks: z.array(auditCheckSchema),
  columns: z.array(procedureColumnSchema),
});

export const procedureWithUserSchema = procedureSchema.extend({
  user: z.object({
    id: z.string(),
    email: z.string(),
    createdAt: z.string(),
  }),
});

export type ProcedureWithUser = z.infer<typeof procedureWithUserSchema>;
export type DryProcedure = z.infer<typeof dryProcedureSchema>;
export type DryProcedureWithUser = z.infer<typeof dryProcedureWithUserSchema>;
export type Procedure = z.infer<typeof procedureSchema>;
export type ProcedureColumn = z.infer<typeof procedureColumnSchema>;
export type SupportColumn = z.infer<typeof supportColumnSchema>;
export type EvaluatedColumn = z.infer<typeof evaluatedColumnSchema>;

/**
 * The column kinds a procedure can hold. This is the source of the string
 * `"analytics"` — the backend conforms to it, not the reverse — so if the literal
 * ever changes, both sides move together.
 */
export const COLUMN_TYPES = [
  "support",
  "evaluated",
  "attribute_test",
  "analytics",
] as const;
export type ColumnType = (typeof COLUMN_TYPES)[number];

export type { AttributeTestColumn } from "./attributeTestColumn";
export type { AnalyticsColumn } from "./analyticsColumn";
export type { AnalyticsColumnResult } from "./analyticsColumnResult";
// Match session related schemas
const documentActionSchema = z.object({
  id: z.string(),
  sourceRowId: z.string(),
  procedureDocumentId: z.string(),
  userId: z.string(),
  action: z.enum(["mark_missing"]).optional(),
  ...timestampFields,
});
export const IssueCategory = z.enum(["test_outcome", "anomaly", "exception"]);
// Exception types configuration
export const EXCEPTION_TYPES = [
  { value: "insufficient_evidence", label: "Insufficient Evidence" },
  { value: "contradictory_evidence", label: "Contradictory Evidence" },
  {
    value: "unusual_transaction_pattern",
    label: "Unusual Transaction Pattern",
  },
  { value: "outlier_amount", label: "Outlier Amount" },
  { value: "duplicate_transaction", label: "Duplicate Transaction" },
  {
    value: "missing_supporting_documentation",
    label: "Missing Supporting Documentation",
  },
  { value: "inconsistent_documentation", label: "Inconsistent Documentation" },
  { value: "late_entry", label: "Late Entry" },
  {
    value: "nonstandard_terms_or_conditions",
    label: "Nonstandard Terms Or Conditions",
  },
  { value: "unexpected_counterparty", label: "Unexpected Counterparty" },
  {
    value: "manual_journal_entry_exception",
    label: "Manual Journal Entry Exception",
  },
  { value: "round_number_or_estimate", label: "Round Number Or Estimate" },
  { value: "general_anomaly", label: "General Anomaly" },
  { value: "untimely_execution", label: "Untimely Execution" },
  { value: "improper_authorization", label: "Improper Authorization" },
  {
    value: "inconsistent_supporting_data",
    label: "Inconsistent Supporting Data",
  },
  {
    value: "access_or_permissions_exception",
    label: "Access Or Permissions Exception",
  },
  { value: "control_not_performed", label: "Control Not Performed" },
  {
    value: "unclear_or_ambiguous_evidence",
    label: "Unclear Or Ambiguous Evidence",
  },
  { value: "misstatement_of_amount", label: "Misstatement Of Amount" },
  { value: "unsupported_entry", label: "Unsupported Entry" },
  { value: "cutoff_misclassification", label: "Cutoff Misclassification" },
  { value: "valuation_error", label: "Valuation Error" },
  { value: "noncompliant_with_policy", label: "Noncompliant With Policy" },
  { value: "evidence_inconsistency", label: "Evidence Inconsistency" },
  { value: "inconclusive_evidence", label: "Inconclusive Evidence" },
  { value: "general_exception", label: "General Exception" },
] as const;

export const IssueSubcategory = z.enum([
  "issues_found",
  "no_issues_found",
  "not_applicable",
  "exhausted_search",
  // Frontend-only: synthesized by attributeTestTranslator for an inconclusive
  // attribute test with no stated performance issue, so it displays as
  // Requires Attention. Never sent by or to the backend. Distinct from the
  // `inconclusive_evidence` control exception, which is Issues Found.
  "inconclusive",
  ...EXCEPTION_TYPES.map((type) => type.value),
] as const);

const CheckResultIssueSchema = z.object({
  issueCategory: IssueCategory,
  issueSubcategory: IssueSubcategory,
  description: z.string(),
});

const CheckResultCitationSchema = z.object({
  id: z.string().uuid(),
  boundingBox: z
    .tuple([Coordinate, Coordinate, Coordinate, Coordinate])
    .nullable(),
  pageNumber: z.number().nullable(),
  pageWidth: z.number().nullable(),
  pageHeight: z.number().nullable(),
  documentId: z.string(),
  documentElement: z.string(),
  encodedCitationString: z.string().optional().nullable(),
  attributeIds: z.array(z.string()),
  shortSummary: z.string().nullable(),
  reasonSpan: z.string().nullable(),
  citationOrder: z.number().optional().nullable(),
  reperformanceStepId: z.string().nullable().optional(),
  attributeTestResultId: z.string().nullable().optional(),
  outcomeSpanStart: z.number().nullable().optional(),
  outcomeSpanEnd: z.number().nullable().optional(),
  isKey: z.boolean().nullable().optional(),
  // Video-specific: timestamp in seconds for video citations
  timestampSeconds: z.number().nullable().optional(),
  createdAt: z.string().or(z.date()),
  updatedAt: z.string().or(z.date()),
});

export const checkResultSchema = z.object({
  id: z.string().uuid(),
  checkId: z.string().nullable(),
  evalColumnId: z.string().nullable(),
  reviewerType: z.enum(["human", "system"]),
  userId: z.string().nullable(),
  decision: z.enum(["pass", "fail", "not_applicable"]),
  reason: z.string(),
  reviewOrder: z.number().int(),
  lockedBy: z.string().nullable(),
  issues: z.array(CheckResultIssueSchema),
  citations: z.array(CheckResultCitationSchema),
  columnVersion: z.number().default(0),
  runId: z.string().nullable().optional(),
  createdAt: z.string().or(z.date()),
  updatedAt: z.string().or(z.date()),
});

export const matchItemSchema = z.object({
  id: z.string(),
  extractionId: z.string().nullable(),
  extractionElementId: z.string().nullable(),
  confidenceScore: z.number(),
  assignments: z.array(
    z.object({
      id: z.string(),
      procedureDocumentId: z.string(),
      assignerId: z.string().nullable(),
      ...timestampFields,
    }),
  ),
  matchReason: z.record(z.string(), z.number()).optional(),
  sourceDocument: sourceDocumentSchema,
  state: z.enum(["rejected", "approved", "pending"]),
  ...timestampFields,
});

const DocumentAssignmentSchema = z.object({
  id: z.string(),
  shortName: z.string(),
  description: z.string().nullable(),
  ...timestampFields,
});

const DocumentState = z.enum([
  "pending",
  "processing",
  "ready",
  "failed",
  "video_pending_parse",
]);

export type UploadedDocument = z.infer<typeof UploadedDocumentSchema>;
export const UploadedDocumentSchema = z.object({
  id: z.string().uuid(),
  fileName: z.string(),
  fileType: FileType,
  fileUrl: z.string(),
  documentAssignments: z.array(DocumentAssignmentSchema).nullable(),
  fileContentHash: z.string(),
  extra: z.record(z.unknown()).default({}),
  parentDocumentId: z.string().nullable().optional(),
  childType: ChildTypeEnum.nullable().optional(),
  state: DocumentState,
  isParsed: z.boolean().default(false),
  ...timestampFields,
});

// Base UploadedDocument schema
export const searchResultSchema = z.object({
  ...UploadedDocumentSchema.shape,
  highlight: z.string(),
});

export const sourceRowSchema = z.object({
  id: z.string(),
  data: z.record(JSONPrimitiveSchema),
  sampleNumber: z.number().nullable(),
  matchItemsState: lifecycleStateSchema,
  testsState: testsStateSchema,
  requiredDocumentsActions: z.array(documentActionSchema),
  checkResults: z.array(checkResultSchema),
  attributeTestResults: z.array(AttributeTestColumnResultSchema),
  // Defaulted to `[]` so a response that predates analytics columns still parses.
  //
  // Element-tolerant for the same reason the table shell's projection of these
  // verdicts is: this array sits beside every other column's results, so a
  // verdict this build cannot read would otherwise fail the source row and take
  // the support, evaluated and attribute-test results down with it — and the
  // same payload backs the whole-session read, where the cost is the workpaper.
  // A dropped verdict leaves its own cell with no answer, which renders as
  // nothing rather than as a pass.
  analyticsResults: z
    .array(AnalyticsColumnResultSchema.nullable().catch(null))
    .default([])
    .transform((rows) => rows.filter((row) => row !== null)),
  matchItems: z.array(matchItemSchema),
  ...timestampFields,
});

export const attributeResultSchema = z.object({
  value: z.union([z.string(), z.boolean(), z.number(), z.null()]),
  unit: z.string().nullable(),
  confidence: z.number().nullable(),
  sourceElementId: z.string().nullable(),
});

export const extractionSchema = z.object({
  id: z.string(),
  data: z.record(z.string(), attributeResultSchema),
  ...timestampFields,
});

export const sessionRequiredDocumentTypeSchema = z.object({
  id: z.string(),
  documentId: z.string(),
  name: z.string().nullable(),
  fetchableDocument: UploadedDocumentSchema.nullable(),
  creatorType: z.enum(["user", "agent"]).nullable(),
  state: z.enum(["approved", "rejected", "pending"]),
  extraction: extractionSchema.nullable(),
  extractions: z.array(extractionSchema),
  /**
   * This file is also an input to an analytics workflow run.
   *
   * One state per document per test table, so a file attached to several samples
   * carries one answer for all of them.
   *
   * Optional rather than defaulted, and optional rather than required. The column
   * is non-nullable server-side and the wire model types it non-optional, so a
   * response without it means the projection stopped sending it — a different
   * fact from `false`, which says the workspace has ruled this file out, and one
   * that must not be rendered as one: `false` empties the parameters track and
   * hides whatever bindings the file answers. Defaulting collapsed the two.
   * Required would be worse than either, because this schema is an element of
   * `documents` and zod throws on the first element that fails: one absent field
   * would take out the whole read and every row with it.
   */
  isAnalyticsInput: z.boolean().optional(),
});

export const sessionSchema = z.object({
  ...drySessionSchema.shape,
  procedure: procedureSchema,
  documents: z.array(sessionRequiredDocumentTypeSchema),
  sourceRows: z.array(sourceRowSchema),
});

export type AuditSession = z.infer<typeof sessionSchema>;
export type CheckResult = z.infer<typeof checkResultSchema>;
export type SourceRow = z.infer<typeof sourceRowSchema>;

// ============================================================================
// Phase 2 (megaquery decomposition) — envelope + pagination schemas.
// Backend serializes via Pydantic `alias_generator=to_camel`, so wire format
// is camelCase. Field names below intentionally match the BE JSON.
// ============================================================================

// Envelope-level procedure: the dry procedure plus the `isEnabled` toggle that
// every Phase-2 consumer needs in order to filter enabled procedures without
// hydrating the full procedure tree.
export const procedureEnvelopeSchema = dryProcedureSchema.extend({
  isEnabled: z.boolean().default(true),
});

export const procedureSessionEnvelopeSchema = z.object({
  id: z.string(),
  controlSessionId: z.string().nullable(),
  procedureId: z.string(),
  userId: z.string().nullable(),
  workspaceId: z.string(),
  name: z.string(),
  state: lifecycleStateSchema,
  documentsState: lifecycleStateSchema,
  testsState: testsStateSchema,
  sourceRowsState: lifecycleStateSchema,
  workPaperState: lifecycleStateSchema,
  longTimeoutsEnabled: z.boolean().default(false),
  createdAt: z.string(),
  updatedAt: z.string(),
  procedure: procedureEnvelopeSchema,
  sourceRowsCount: z.number().int(),
  documentsCount: z.number().int(),
  pendingDocumentsCount: z.number().int(),
});

// Document slice envelope with denormalised sample_numbers.
export const sessionDocumentWithSampleNumbersSchema =
  sessionRequiredDocumentTypeSchema.extend({
    sampleNumbers: z.array(z.number().int()).default([]),
  });

export const procedureSessionDocumentsResponseSchema = z.object({
  documents: z.array(sessionDocumentWithSampleNumbersSchema).default([]),
});

export type ProcedureSessionDocumentsResponse = z.infer<
  typeof procedureSessionDocumentsResponseSchema
>;
